import {
  CandidateStatus,
  QuarantineCompany,
  QuarantineUniversity,
  QuarantineCities,
  RoleStatus,
} from "./Models";
import { GetCandidateCv } from "./api/candidates.api";

export const RoleStatusType: {
  rejected: string;
  hired: string;
  applied: string;
} = {
  applied: "Applied",
  rejected: "Disqualified",
  hired: "Offered",
};
export const dateConverter = (date: string, withTime?: boolean): string => {
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  if (withTime) {
    options.hour = "numeric";
    options.minute = "numeric";
    options.hour12 = true;
  }
  const convertedDate = new Date(date).toLocaleDateString("en-US", options);
  return convertedDate;
};

export function dateDiff(startDate: Date, endDate: Date | null): number {
  startDate = new Date(startDate);
  endDate = endDate == null ? new Date() : new Date(endDate);
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    return 0;
  }

  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth(); // 0-based (Jan = 0, Dec = 11)

  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth(); // 0-based

  // Total months difference
  return (endYear - startYear) * 12 + (endMonth - startMonth);
}
export const getAvatarName = (username: string): string => {
  const splitUsername = username.split(" ");
  return (
    splitUsername[0][0] + splitUsername[splitUsername.length - 1][0]
  ).toUpperCase();
};

export function getColorShade(rankNumber: number, university = false) {
  const maxQualityScore = 5;

  const darkOrange = { r: 255, g: 140, b: 0 };
  const lightOrange = { r: 255, g: 230, b: 200 };

  const darkBlue = { r: 0, g: 0, b: 139 };
  const lightBlue = { r: 173, g: 216, b: 230 };

  const darkYellow = { r: 204, g: 204, b: 0 };
  const lightYellow = { r: 255, g: 255, b: 224 };

  const factor = (rankNumber - 1) / maxQualityScore;

  function interpolateColor(
    darkColor: { r: number; g: number; b: number },
    lightColor: { r: number; g: number; b: number }
  ) {
    const r = Math.round(darkColor.r + factor * (lightColor.r - darkColor.r));
    const g = Math.round(darkColor.g + factor * (lightColor.g - darkColor.g));
    const b = Math.round(darkColor.b + factor * (lightColor.b - darkColor.b));
    return `rgb(${r}, ${g}, ${b})`;
  }

  if (university) {
    const postGrad = interpolateColor(darkYellow, lightYellow);
    const grad = interpolateColor(darkOrange, lightOrange);
    return { postGrad, grad };
  } else {
    return interpolateColor(darkBlue, lightBlue);
  }
}

export const getCompanyRankingShade = (rankNumber: number) => {
  return getColorShade(rankNumber, false) as string;
};

export const getUniversityRankingShade = (rankNumber: number) => {
  return getColorShade(rankNumber, true) as { postGrad: string; grad: string };
};

export const getStyleForUniversity = (ranking: number, postGrad: boolean) => {
  let color;
  let background;
  const shade = getColorShade(ranking, true) as {
    postGrad: string;
    grad: string;
  };
  if (ranking === 0) {
    color = "white";
    background = "#A9A9A9";
  } else if (ranking === 1 || ranking === 2) {
    color = "white";
    background = postGrad ? shade.postGrad : shade.grad;
  } else {
    color = "#4B5563";
    background = postGrad ? shade.postGrad : shade.grad;
  }

  return { background, color };
};

export const getStyleForCompany = (ranking: number) => {
  let color;
  let background;

  if (ranking === 0) {
    color = "white";
    background = "#A9A9A9";
  } else if (ranking <= 3) {
    color = "white";
    background = getColorShade(ranking, false) as string;
  } else {
    color = "#4B5563";
    background = getColorShade(ranking, false) as string;
  }

  return { background, color };
};

// ============================================================================
// QUARANTINE TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if an item is a QuarantineCompany
 * Companies have a companyId and companyLocations array
 */
export function isQuarantineCompany(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities | any
): item is QuarantineCompany {
  return (
    item &&
    typeof item === "object" &&
    "companyId" in item &&
    item.companyId !== undefined &&
    item.companyId !== null
  );
}

/**
 * Type guard to check if an item is a QuarantineUniversity
 * Universities have abbreviation and/or degrees and cities array
 */
export function isQuarantineUniversity(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities | any
): item is QuarantineUniversity {
  return (
    item &&
    typeof item === "object" &&
    ("abbreviation" in item || ("degrees" in item && "cities" in item))
  );
}

/**
 * Type guard to check if an item is a QuarantineCity
 * Cities have actiongpt and stateId properties
 */
export function isQuarantineCity(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities | any
): item is QuarantineCities {
  return (
    item &&
    typeof item === "object" &&
    "actiongpt" in item &&
    "stateId" in item
  );
}

/**
 * Get the type of quarantine item
 */
export type QuarantineType = "company" | "university" | "city";

export function getQuarantineType(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities | any
): QuarantineType {
  if (isQuarantineCompany(item)) return "company";
  if (isQuarantineUniversity(item)) return "university";
  return "city";
}

// ============================================================================
// QUARANTINE UTILITY FUNCTIONS
// ============================================================================

/**
 * Get the ID from any quarantine item type
 */
export function getQuarantineId(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities
): string {
  return item.id;
}

/**
 * Get the name from any quarantine item type
 */
export function getQuarantineName(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities
): string {
  return item.name;
}

/**
 * Get the confirmed status from any quarantine item type
 */
export function getQuarantineConfirmedStatus(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities
): boolean {
  return item.isConfirmed ?? false;
}

/**
 * Get alternate names from any quarantine item type
 */
export function getQuarantineAlternateNames(
  item: QuarantineCompany | QuarantineUniversity | QuarantineCities
): string | undefined {
  return item.alternateNames;
}


// ============================================================================
// OTHER UTILITY FUNCTIONS
// ============================================================================

export const getNextStatus = (
  roleStatuses: RoleStatus[],
  candidateStatuses: CandidateStatus[]
) => {
  if (!roleStatuses || roleStatuses.length === 0) return;
  let hiredOrRejected = false;
  const latestStatus = roleStatuses.find(
    (s) =>
      s.id === candidateStatuses[candidateStatuses.length - 1]?.roleStatusId
  );
  let nextStatusIndex = 0;
  if (latestStatus) {
    hiredOrRejected =
      latestStatus.name === RoleStatusType.hired ||
      latestStatus.name === RoleStatusType.rejected;
    if (hiredOrRejected) return;

    nextStatusIndex =
      roleStatuses.findIndex((rs) => rs.order === latestStatus.order) + 1;
  }
  if (nextStatusIndex > roleStatuses.length - 1) return;

  const nextStatusHiredOrRejected =
    roleStatuses[nextStatusIndex].name === RoleStatusType.rejected ||
    roleStatuses[nextStatusIndex].name === RoleStatusType.hired;

  const containsHiredAndRejected =
    roleStatuses.some((rs) => rs.name === RoleStatusType.hired) &&
    roleStatuses.some((rs) => rs.name === RoleStatusType.rejected);
  return nextStatusHiredOrRejected ||
    (containsHiredAndRejected && nextStatusIndex < roleStatuses.length - 2)
    ? [
        roleStatuses[nextStatusIndex].name === RoleStatusType.rejected
          ? roleStatuses[nextStatusIndex + 1].id
          : roleStatuses[nextStatusIndex].id,
        roleStatuses.find((rs) => rs.name === RoleStatusType.rejected)?.id,
      ]
    : [roleStatuses[nextStatusIndex].id];
};

export function areArraysEqual<T extends Record<string, any>>(
  arr1: T[],
  arr2: T[]
): boolean {
  if (arr1.length !== arr2.length) return false;

  return arr1.every((obj1, index) => {
    const obj2 = arr2[index];

    return areObjectsEqual(obj1, obj2);
  });
}

export function areObjectsEqual<T>(obj1: T, obj2: T): boolean {
  if (
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  ) {
    return obj1 === obj2;
  }

  if (obj1 === null || obj2 === null) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    const val1 = (obj1 as Record<string, any>)[key];
    const val2 = (obj2 as Record<string, any>)[key];

    if (!areObjectsEqual(val1, val2)) {
      return false;
    }
  }

  return true;
}

export async function downloadCv(
  token: string,
  candidateId: string,
  candidateName: string
) {
  try {
    const cvBlob = await GetCandidateCv(candidateId, token);

    const arrayBuffer = await cvBlob.arrayBuffer();
    const byteArray = new Uint8Array(arrayBuffer);

    let extension = "";

    const pdfSignature = [0x25, 0x50, 0x44, 0x46];
    const isPdf = pdfSignature.every((byte, i) => byte === byteArray[i]);

    const docxSignature = [0x50, 0x4b];
    const isDocx = docxSignature.every((byte, i) => byte === byteArray[i]);

    if (isPdf) {
      extension = "pdf";
    } else if (isDocx) {
      extension = "docx";
    } else {
      throw new Error("Unsupported file format");
    }

    const blobUrl = window.URL.createObjectURL(cvBlob);
    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;

    downloadLink.download = `resume_${candidateName}.${extension}`;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error handling CV:", error);
  }
}
